
import { Component, Vue } from 'vue-property-decorator';
import ExcelJS from 'exceljs';
import { mergeInterwinnerResults } from '@/modules/results/controllers/interwinnerResult';
import Dialog, { DialogData } from '@/components/dialog/Dialog.vue';
import TextBoxCard, { TextBoxData } from '@/components/textbox/TextBoxCard.vue';
import { ErrorEnum } from '@/models/errorEnum';
import { ErrorInterwinnerFile } from '@/modules/results/models/errorInterwinnerFile';
import { TextBoxEnum } from '@/models/textBoxEnum';
import { SortMethodsEnum } from '@/modules/results/models/sortMethodsEnum';
import { ErrorRadionuclide } from '@/modules/results/models/errorRadionuclide';
import { ErrorFile } from '@/modules/results/models/errorFile';

@Component({
  components: { Dialog, TextBoxCard }
})
export default class Interwinner extends Vue {
  protected readonly SORT_METHODS_ENUM = SortMethodsEnum;
  protected readonly RADIONULIDES_LIST =
    'CS-137;PU-239;PU-240;AM-241;NP-239;U-235;TH-231;TH-234;PA-234M;U-234;PB-214;BI-214;K-40;AC-228;RA-224;PB-212;BI-212;TL-208';
  protected dragover = false;
  protected uploadedFiles: File[] = [];
  protected dialogError: DialogData = {
    isOpen: false,
    title: '',
    content: ''
  };
  protected isDownloadReady = false;
  protected inputFiles!: HTMLInputElement;
  protected excel!: ExcelJS.Buffer;
  protected readonly EXPORT_FILENAME = 'results.xlsx';
  protected textBoxData: TextBoxData = {
    title: TextBoxEnum.ERROR,
    content: ''
  };
  protected sortMethods = Object.values(this.SORT_METHODS_ENUM);
  protected sortMethod = SortMethodsEnum.DEFAULT;
  protected radionuclidesOrder = this.RADIONULIDES_LIST;

  protected mounted(): void {
    this.inputFiles = document.createElement('input');
    this.inputFiles.accept = 'text/plain';
    this.inputFiles.type = 'file';
    this.inputFiles.multiple = true;
    this.inputFiles.oninput = this.onInputFiles;
  }

  /**
   * The user has dropped files into the drag and drop card.
   */
  protected onDropFiles(e: DragEvent): void {
    this.dragover = false;
    if (e?.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      this.saveFiles(e.dataTransfer.files);
    }
  }

  /**
   * The user has input files after clicked into the drag and drop card. Reset inputFiles value.
   */
  protected onInputFiles(): void {
    this.saveFiles(this.inputFiles.files);
    this.inputFiles.value = '';
  }

  /**
   * The user has input or dropped files. Checks and saves files.
   */
  protected saveFiles(files: FileList | null): void {
    if (files) {
      let tmpFileList = [];
      for (let index = 0; index < files.length; index++) {
        const file = files.item(index) as File;
        if (!file.type.startsWith('text/plain')) {
          this.openAndSetDialogErrorContent(new Error(ErrorEnum.INVALID_FILE_EXTENSION));
          return;
        }
        if (this.uploadedFiles.some((uploadedFile) => uploadedFile.name === file.name)) {
          this.openAndSetDialogErrorContent(new ErrorFile(ErrorEnum.DUPLICATED_FILE_NAME, file.name));
          return;
        }
        tmpFileList.push(file);
      }
      tmpFileList.sort((a, b) => a.name.localeCompare(b.name));
      this.uploadedFiles = this.uploadedFiles.concat(tmpFileList);
    }
  }

  protected merge(): void {
    let rnListOrder;
    if (this.sortMethod === SortMethodsEnum.CUSTOM) {
      if (!this.isValidRadionuclidesOrder(this.radionuclidesOrder)) {
        this.openAndSetDialogErrorContent(new Error(ErrorEnum.INVALID_RADIONUCLIDE_FORMAT));
        return;
      }
      rnListOrder = this.radionuclidesOrder.split(';');
    }

    mergeInterwinnerResults(this.uploadedFiles, rnListOrder)
      .then((res) => {
        this.excel = res;
        this.isDownloadReady = true;
        this.textBoxData.title = TextBoxEnum.SUCCESS;
        this.textBoxData.content = this.$i18n.t(`results.Merger.Interwinner.successMessage`).toString();
      })
      .catch((error: Error) => {
        this.openAndSetDialogErrorContent(error);
      });
  }

  protected openAndSetDialogErrorContent(error: Error): void {
    if (!Object.keys(ErrorEnum).includes(error.message)) {
      error.message = ErrorEnum.INTERNAL_SERVER_ERROR;
      this.dialogError.title = this.$i18n.t(`common.InternalError.fileError.${error.message}.title`).toString();
      this.dialogError.content = this.$i18n.t(`common.InternalError.fileError.${error.message}.content`).toString();
    } else {
      this.dialogError.title = this.$i18n.t(`results.Merger.Interwinner.fileError.${error.message}.title`).toString();
      this.dialogError.content = this.getInterwinnerMesageByError(error);
    }
    this.dialogError.isOpen = true;
  }

  protected getInterwinnerMesageByError(error: Error): string {
    let message = '';
    if (error instanceof ErrorInterwinnerFile) {
      const errorInterwinner = error as ErrorInterwinnerFile;
      message = this.$i18n
        .t(`results.Merger.Interwinner.fileError.${error.message}.content`, {
          fileName: errorInterwinner.fileName,
          unitsReferenceFileName: errorInterwinner.unitsReferenceFileName,
          unitsReferenceEnum: errorInterwinner.unitsReferenceEnum.toLowerCase()
        })
        .toString();
    } else if (error instanceof ErrorRadionuclide) {
      const errorRadionuclide = error as ErrorRadionuclide;
      message = this.$i18n
        .t(`results.Merger.Interwinner.fileError.${error.message}.content`, {
          rnName: errorRadionuclide.rnName
        })
        .toString();
    } else if (error instanceof ErrorFile) {
      const errorFile = error as ErrorFile;
      message = this.$i18n
        .t(`results.Merger.Interwinner.fileError.${error.message}.content`, {
          fileName: errorFile.fileName
        })
        .toString();
    } else {
      message = this.$i18n.t(`results.Merger.Interwinner.fileError.${error.message}.content`).toString();
    }

    return message;
  }

  protected downloadCsv(): void {
    let hiddenElement = document.createElement('a');
    const data = new Blob([this.excel], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    hiddenElement.href = URL.createObjectURL(data);
    hiddenElement.target = '_blank';
    hiddenElement.download = this.EXPORT_FILENAME;
    hiddenElement.click();
  }

  protected removeFile(fileName: string): void {
    this.uploadedFiles = this.uploadedFiles.filter((file) => file.name !== fileName);
  }

  protected reset(): void {
    this.isDownloadReady = false;
    this.uploadedFiles = [];
    this.sortMethod = SortMethodsEnum.DEFAULT;
    this.radionuclidesOrder = this.RADIONULIDES_LIST;
  }

  protected isValidRadionuclidesOrder(value: string): boolean {
    const reg = /^(([a-zA-Z]{1,2}-[0-9]{1,3}[mM]?;)+)?([a-zA-Z]{1,2}-[0-9]{1,3}[mM]?)$/;
    return reg.test(value);
  }
}
